import ApiServices, { InstanceKeys } from "@/api/apexchat.api.services";
import { LeadAPI, uploadPublicAppointmentAttachment } from "@/api/endpoints";
import { cloneDeep } from "@/services/generic.methods";
import storage, {
  getParsedStorage,
} from "@/services/storage";
import { createSlice, createSelector } from "@reduxjs/toolkit";

const getRecordsPerPage = (records) => {
  const mappedRecords = {};
  const storage = getParsedStorage("PageLimits");
  for (let key in records) {
    mappedRecords[key] = storage?.[key] || records?.[key] || 10;
  }
  return mappedRecords;
};

export const crmLeadSlice = createSlice({
  name: "crmLeads",
  initialState: {
    lead: null,
    leadById: {},
    leadColumns: [],
    dropdownOptions: {},
    recordsPerPage: getRecordsPerPage({
      leads: 10,
      import: 10,
    }),
    leadGridSettings: {
      columns: null,
      columnFilters: null,
      sortingInfo: null,
    },
    customCodes: [],
    leadWorkflows: [],
    searchFields: [],
    taskSearchFields: [],
    smsSearchFields: [],
    emailSearchFields: [],
    appointmentSearchFields: [],
    appointmentLeadSearchFields: [],
    leadUpdateFields: [],
    leadScore: {},
    leadUnreadSMSCount: 0,
    leadUnreadEmailCount: 0,
    bookingShareDraft: null,
  },

  reducers: {
    setLeadToState: (state, action) => {
      state.lead = action.payload;
    },
    setCustomCodesToState: (state, action) => {
      state.customCodes = action.payload;
    },
    setLeadToStateById: (state, action) => {
      const lead = action.payload;
      if (lead?.id) {
        state.leadById[lead.id] = lead;
      }
    },
    setRemoveLeadFromStateById: (state, action) => {
      const lead = action.payload;
      if (lead?.id) {
        const leads = cloneDeep(state.leadById);
        if (leads?.[lead.id]) {
          delete leads[lead.id];
        }
        state.leadById = leads;
      }
    },
    setLeadAttachments: (state, action) => {
      const { leadId, attachments } = action.payload;
      state.leadById[leadId].leadAttachments = [...attachments];
    },
    setLeadColumnsToState: (state, action) => {
      state.leadColumns = action.payload;
    },
    setLeadGridSettingsToState: (state, action) => {
      state.leadGridSettings = action.payload;
    },
    setLeadWorkflowsToState: (state, action) => {
      state.leadWorkflows = action.payload;
    },

    /**
     * @name @setRecordsPerPage
     * @description to keep the leads count per page
     **/
    setRecordsPerPage: (state, action) => {
      storage.updateStorageItem("PageLimits", action.payload);
      const recordsPerPage = JSON.parse(JSON.stringify(state.recordsPerPage));
      const payload = {
        ...recordsPerPage,
        ...action.payload,
      };
      state.recordsPerPage = payload;
    },
    setLeadsSearchFields: (state, action) => {
      state.searchFields = action.payload;
    },
    setTaskSearchFields: (state, action) => {
      state.taskSearchFields = action.payload;
    },
    setSMSSearchFields: (state, action) => {
      state.smsSearchFields = action.payload;
    },
    setEmailSearchFields: (state, action) => {
      state.emailSearchFields = action.payload;
    },
    setAppointmentSearchFields: (state, action) => {
      state.appointmentSearchFields = action.payload;
    },
    setAppointmentLeadSearchFields: (state, action) => {
      state.appointmentLeadSearchFields = action.payload;
    },
    removeAppointmentSearchField: (state, action) => {
      const keyToRemove = action.payload;
      state.appointmentSearchFields = state.appointmentSearchFields.filter(
        (field) => field.key.toLowerCase() !== keyToRemove.toLowerCase()
      );
    },
    removeAppointmentLeadSearchField: (state, action) => {
      const keyToRemove = action.payload;
      state.appointmentLeadSearchFields = state.appointmentLeadSearchFields.filter(
        (field) => field.key.toLowerCase() !== keyToRemove.toLowerCase()
      );
    },
    setLeadUpdateFields: (state, action) => {
      state.leadUpdateFields = action.payload;
    },
    setLeadScoringDetailsToState: (state, action) => {
      const data = action.payload || {};
      if (data) {
        data.totalScore = (data?.items || []).reduce(
          (acc, section) => acc + (section?.leadScoringRule?.score || 0),
          0,
        );
        state.leadScore = data;
      }
    },
    setLeadUreadSMSCount: (state, action) => {
      state.leadUnreadSMSCount = action.payload;
    },
    setLeadUreadEmailCount: (state, action) => {
      state.leadUnreadEmailCount = action.payload;
    },
    setBookingShareDraft: (state, action) => {
      state.bookingShareDraft = action.payload || null;
    },
    clearBookingShareDraft: (state) => {
      state.bookingShareDraft = null;
    },
  },
});

export const {
  setLeadToState,
  setLeadToStateById,
  setRemoveLeadFromStateById,
  setLeadColumnsToState,
  setLeadGridSettingsToState,
  setLeadAttachments,
  setRecordsPerPage,
  setLeadWorkflowsToState,
  setCustomCodesToState,
  setLeadsSearchFields,
  setSMSSearchFields,
  setEmailSearchFields,
  setAppointmentSearchFields,
  setAppointmentLeadSearchFields,
  removeAppointmentSearchField,
  removeAppointmentLeadSearchField,
  setLeadUpdateFields,
  setLeadScoringDetailsToState,
  setLeadUreadSMSCount,
  setLeadUreadEmailCount,
  setTaskSearchFields,
  setBookingShareDraft,
  clearBookingShareDraft,
} = crmLeadSlice.actions;

export const selectLead = (state) => {
  return state.crmLeads;
};
export const getCustomCodes = (state) => {
  return state.crmLeads?.customCodes;
};

export const selectRawLead = (state) => state.crmLeads?.lead;
export const selectRawLeadById = (state) => state.crmLeads?.leadById;
export const selectDropdownUsers = (state) => state.dropdownOptions?.dropdownOptions?.users;

export const getLeadInfo = createSelector(
  [selectRawLead, selectDropdownUsers],
  (lead, users) => {
    if (!lead) return lead;
    if (lead.assignedTo && users?.length) {
      const matchedUser = users.find(
        (u) => Number(u.sourceId) === Number(lead.assignedTo) || u.id === lead.assignedTo || u.userId === lead.assignedTo
      );
      if (matchedUser) {
        return { ...lead, assignedTo: matchedUser.id };
      }
    }
    return lead;
  }
);

export const getLeadInfoById = createSelector(
  [selectRawLeadById, selectDropdownUsers],
  (leads, users) => {
    if (!leads) return leads;
    if (!users?.length) return leads;
    
    const mappedLeads = {};
    let modified = false;
    for (const id in leads) {
      const lead = leads[id];
      if (lead?.assignedTo) {
        const matchedUser = users.find(
          (u) => Number(u.sourceId) === Number(lead.assignedTo) || u.id === lead.assignedTo || u.userId === lead.assignedTo
        );
        if (matchedUser) {
          mappedLeads[id] = { ...lead, assignedTo: matchedUser.id };
          modified = true;
          continue;
        }
      }
      mappedLeads[id] = lead;
    }
    return modified ? mappedLeads : leads;
  }
);
export const getLeadColumns = (state) => {
  return state.crmLeads.leadColumns;
};
export const getLeadGridSettings = (state) => {
  return state.crmLeads.leadGridSettings;
};

export const uploadLeadAttachment = (payload, params, httpConfig) => {
  let url = `${LeadAPI.uploadAttachment}&${params}`;
  return ApiServices.postAttachements(
    url,
    payload,
    InstanceKeys.crmInstance,
    httpConfig,
  );
};
export const uploadPublicAppointmentLogo = (payload, params, httpConfig) => {
  let url = `${uploadPublicAppointmentAttachment}&${params}`;
  return ApiServices.put(url, payload, InstanceKeys.crmInstance, httpConfig);
};

export const selectLeadsRecordsCount = (state) => {
  return state.crmLeads?.recordsPerPage;
};

export const selectLeadWorkflows = (state) => {
  return state.crmLeads?.leadWorkflows;
};
export const selectLeadSeachFields = (state) => {
  return state.crmLeads?.searchFields;
};
export const selectSMSSeachFields = (state) => {
  return state.crmLeads?.smsSearchFields;
};
export const selectEmailSeachFields = (state) => {
  return state.crmLeads?.emailSearchFields;
};
export const selectAppointmentSeachFields = (state) => {
  return state.crmLeads?.appointmentSearchFields;
};
export const selectAppointmentLeadSeachFields = (state) => {
  return state.crmLeads?.appointmentLeadSearchFields;
};
export const selectLeadUpdateFields = (state) => {
  return state.crmLeads?.leadUpdateFields;
};
export const selectLeadScoreDetails = (state) => {
  return state.crmLeads?.leadScore;
};

export const getLeadUnreadSMSCount = (state) => {
  return state.crmLeads?.leadUnreadSMSCount;
};
export const getLeadUnreadEmailCount = (state) => {
  return state.crmLeads?.leadUnreadEmailCount;
};
export const getBookingShareDraft = (state) => {
  return state.crmLeads?.bookingShareDraft;
};
export const selectTaskSeachFields = (state) => {
  return state.crmLeads?.taskSearchFields;
};

export default crmLeadSlice.reducer;
